import { useRef, useState, type KeyboardEvent, type PointerEvent, type ReactElement } from "react";
import { pixelType } from "odyssey-shared";
import { say } from "../../words.ts";
import { classes } from "./classes.ts";
import { centreOffset, logical } from "./metrics.ts";

/**
 * A slider (the design's `components/controls/Slider.jsx`, P1.9-T49): a track 4 tall in ink with a 1 px edge, a brass
 * fill and a vellum thumb 4 by 10, the value beside it in the pixel face. A press on the track jumps to the point and
 * a drag follows the pointer; the arrow keys move by a step, ten with Shift, and Home and End go to the ends, each key
 * it answers prevented so the input router and Phaser never move the character with it. `onChange` follows every move;
 * `onCommit` hears the value a drag ends on and each key's, so a page keeps a setting once, not on every move.
 * `muted` greys the fill and the thumb and reads "Off" while keeping the level. It reads to assistive technology as a
 * slider with its value. The track sits in the middle of the value's line by a whole number of logical pixels
 * (`SLIDER_ROW`), never by CSS centring, which puts a 10 px track half a pixel off in an odd line.
 */

/**
 * The slider's height and its parts' in logical pixels.
 */
export const SLIDER_METRICS = { height: 10, track: 4, trackTop: 3, fill: 2, fillTop: 4, thumb: 4, valueWidth: 22, gap: 4 } as const;

/**
 * The height of the slider with its value beside it, in logical pixels: the taller of the slider and the value's
 * line in the pixel face.
 */
export const SLIDER_ROW = Math.max(SLIDER_METRICS.height, pixelType("label").lineHeight);

/**
 * A value held to the range and rounded to the step.
 *
 * @param value The value.
 * @param min The least.
 * @param max The most.
 * @param step The step.
 * @returns The value on the range's steps.
 */
export function sliderClamp(value: number, min: number, max: number, step: number): number {
  return Math.min(max, Math.max(min, Math.round((value - min) / step) * step + min));
}

/**
 * The value a key moves a slider to.
 *
 * @param key The key.
 * @param shift Whether Shift is held, moving ten steps.
 * @param value The value now.
 * @param min The least.
 * @param max The most.
 * @param step The step.
 * @returns The new value, or null when the key moves nothing.
 */
export function sliderValueForKey(key: string, shift: boolean, value: number, min: number, max: number, step: number): number | null {
  const by = shift ? step * 10 : step;
  switch (key) {
    case "ArrowRight":
    case "ArrowUp":
      return sliderClamp(value + by, min, max, step);
    case "ArrowLeft":
    case "ArrowDown":
      return sliderClamp(value - by, min, max, step);
    case "Home":
      return min;
    case "End":
      return max;
    default:
      return null;
  }
}

/**
 * Where the thumb's left edge sits on a track, in whole logical pixels.
 *
 * @param width The track's width.
 * @param value The value.
 * @param min The least.
 * @param max The most.
 * @returns The offset from the track's left edge.
 */
export function sliderThumbOffset(width: number, value: number, min: number, max: number): number {
  return max === min ? 0 : Math.round(((width - SLIDER_METRICS.thumb) * (value - min)) / (max - min));
}

/**
 * A slider.
 *
 * @param props `describedBy`, the id of an element that describes the slider, when it has one; the accessible `label`, the `value`, `onChange` with each new value, the range (`min`, `max`, `step`),
 *   the track's `width` in logical pixels, `muted` and `disabled`, and `onCommit` with the value a drag ends on or a
 *   key sets.
 * @returns The slider and its value.
 */
export function Slider({ label, value, onChange, onCommit, min = 0, max = 100, step = 1, width = 100, muted = false, disabled = false, describedBy }: { readonly describedBy?: string | undefined; readonly label: string; readonly value: number; readonly onChange: (value: number) => void; readonly onCommit?: (value: number) => void; readonly min?: number; readonly max?: number; readonly step?: number; readonly width?: number; readonly muted?: boolean; readonly disabled?: boolean }): ReactElement {
  const track = useRef<HTMLSpanElement | null>(null);
  const [focus, setFocus] = useState(false);
  const latest = useRef(value);
  latest.current = value;
  const off = muted || disabled;
  const x = sliderThumbOffset(width, value, min, max);

  /**
   * Sets the value from a pointer's position over the track.
   *
   * @param clientX The pointer's x in CSS pixels.
   */
  const fromPointer = (clientX: number): void => {
    const box = track.current?.getBoundingClientRect();
    if (box === undefined || box.width === 0) {
      return;
    }
    const next = sliderClamp(min + ((clientX - box.left) / box.width) * (max - min), min, max, step);
    if (next !== latest.current) {
      latest.current = next;
      onChange(next);
    }
  };

  /**
   * Ends a drag: the value it ends on is committed.
   */
  const onRelease = (): void => {
    onCommit?.(latest.current);
  };

  /**
   * Starts a drag: jumps to the point and follows the pointer while it is captured.
   *
   * @param event The press.
   */
  const onPointerDown = (event: PointerEvent<HTMLSpanElement>): void => {
    if (disabled) {
      return;
    }
    event.preventDefault();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    fromPointer(event.clientX);
  };

  /**
   * Follows a drag.
   *
   * @param event The move.
   */
  const onPointerMove = (event: PointerEvent<HTMLSpanElement>): void => {
    if (!disabled && event.currentTarget.hasPointerCapture?.(event.pointerId) === true) {
      fromPointer(event.clientX);
    }
  };

  /**
   * Moves the value by a key and prevents the keys it answers.
   *
   * @param event The key press.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
    if (disabled) {
      return;
    }
    const next = sliderValueForKey(event.key, event.shiftKey, value, min, max, step);
    if (next === null) {
      return;
    }
    event.preventDefault();
    if (next !== value) {
      onChange(next);
    }
    onCommit?.(next);
  };

  return (
    <span className="ot-slider-row" data-kit="slider-row" style={{ display: "inline-flex", alignItems: "flex-start", height: logical(SLIDER_ROW), gap: logical(SLIDER_METRICS.gap) }}>
      <span
        ref={track}
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={label}
        aria-describedby={describedBy}
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuetext={muted ? "Off" : String(value)}
        aria-disabled={disabled || undefined}
        className={classes("ot-slider", focus && "is-focus", off && "is-off")}
        data-kit="slider"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onLostPointerCapture={onRelease}
        onKeyDown={onKeyDown}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
        style={{ position: "relative", width: logical(width), height: logical(SLIDER_METRICS.height), marginTop: logical(centreOffset(SLIDER_ROW, SLIDER_METRICS.height)), flex: "none", cursor: disabled ? "default" : "pointer", touchAction: "none" }}
      >
        <span className="ot-slider__track" style={{ position: "absolute", left: 0, right: 0, top: logical(SLIDER_METRICS.trackTop), height: logical(SLIDER_METRICS.track) }} />
        <span className="ot-slider__fill" data-kit="slider-fill" style={{ position: "absolute", left: logical(1), top: logical(SLIDER_METRICS.fillTop), width: logical(Math.max(0, x)), height: logical(SLIDER_METRICS.fill) }} />
        <span className="ot-slider__thumb" data-kit="slider-thumb" style={{ position: "absolute", left: logical(x), top: 0, width: logical(SLIDER_METRICS.thumb), height: logical(SLIDER_METRICS.height) }} />
      </span>
      <span className="ot-slider__value" data-kit="slider-value" aria-hidden="true" style={{ width: logical(SLIDER_METRICS.valueWidth), marginTop: logical(centreOffset(SLIDER_ROW, pixelType("label").lineHeight)) }}>
        {muted ? say("settings.audio.off") : value}
      </span>
    </span>
  );
}
